'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { HeaderLogo } from '@eliadmin/core/src/components/header/header-logo';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal px-3 py-1 last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link prefetchOnHover href={category.slug ?? '#'} className="flex-1" prefetch={false}>
          <SheetClose
            className={cn(
              'text-left text-xl font-semibold',
              layer === 1 && 'text-[16px] text-lg font-medium',
              layer && layer > 1 && 'text-sm font-normal',
            )}
          >
            {category.name}
          </SheetClose>
        </Link>
        {hasChildren && (
          <AccordionTrigger className="h-5 w-5" iconstyles="w-5 h-5 text-primary flex-1" />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {hasChildren &&
            category.childs?.map?.((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const { t } = useTranslations();

  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label="Otvoriť menu"
          className={cn('text-primary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          {HEADER_CONFIG?.sidebar?.triggerIcon}
        </SheetTrigger>
        <SheetContent
          side={'right'}
          className={cn(
            'flex flex-col items-center justify-start gap-14 overflow-scroll pt-[150px]',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'absolute right-4 top-4 w-12 h-12 bg-primary flex items-center justify-center',
            icon: 'text-white h-6 w-6',
          }}
        >
          <HeaderLogo generalLayout={generalLayout} />
          {!!props.menu && (
            <>
              <div className="flex w-full flex-col items-start justify-center gap-8">
                {props.menu?.map?.((item, index) => {
                  const href = item.path ? getSafeHref(item.path) : undefined;
                  const isRelative = href ? isRelativeHref(href) : false;

                  return (
                    <SheetClose key={index} asChild>
                      {href ? (
                        isRelative ? (
                          <Link prefetchOnHover
                            href={href}
                            className="flex w-full items-center justify-center text-center"
                            prefetch={false}
                          >
                            <div className={cn('text-base font-medium', 'text-white')}>
                              {item.name}
                            </div>
                          </Link>
                        ) : (
                          <a
                            href={href}
                            className="flex w-full items-center justify-center text-center"
                          >
                            <div className={cn('text-base font-medium', 'text-white')}>
                              {item.name}
                            </div>
                          </a>
                        )
                      ) : (
                        <span className="flex w-full items-center justify-center text-center">
                          <div className={cn('text-base font-medium', 'text-white')}>{item.name}</div>
                        </span>
                      )}
                    </SheetClose>
                  );
                })}
              </div>
            </>
          )}
          <SheetClose asChild>
            <Button asChild variant={'outline'} className="mx-auto text-white hover:text-white">
              <Link prefetchOnHover prefetch={false} href={'/kontakt'}>
                {t('text612')}
              </Link>
            </Button>
          </SheetClose>
          <SocialIcons
            className={{
              wrapper: 'relative z-20 items-center justify-center',
              button: 'border-none p-0',
              icon: 'h-8 w-8 fill-white text-white',
            }}
          />
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
